import {
  CREATIVE_WORKS_CATALOG_API_ORIGIN,
  CREATIVE_WORKS_SEARCH_AT,
  CREATIVE_WORKS_SEARCH_ID_LOOKUP_SIZE,
  CREATIVE_WORKS_SEARCH_PAGE_SIZE,
  CREATIVE_WORKS_SEARCH_PATH,
} from './constants';
import { creativeWorksCatalogRequests } from './creativeWorksSearchClient';

import type {
  CreativeWorksCatalogSearchApiResponse,
  CreativeWorksCatalogResultRaw,
} from './interfaces';

const CREATIVE_WORKS_SEARCH_BASE = `${CREATIVE_WORKS_CATALOG_API_ORIGIN}${CREATIVE_WORKS_SEARCH_PATH}`;

export interface CreativeWorksCatalogSearchPageResponse {
  results: CreativeWorksCatalogResultRaw[];
  pagination?: CreativeWorksCatalogSearchApiResponse['pagination'];
}

const buildSearchParams = (
  query: string,
  options?: {
    originalFormat?: string;
    page?: number;
    pageSize?: number;
  }
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('q', query.trim());
  params.set('fo', 'json');
  params.set('c', String(options?.pageSize ?? CREATIVE_WORKS_SEARCH_PAGE_SIZE));
  params.set('sp', String(options?.page ?? 1));
  params.set('at', CREATIVE_WORKS_SEARCH_AT);

  const trimmedFormat = options?.originalFormat?.trim();
  if (trimmedFormat) {
    const formatFacets = trimmedFormat
      .split('|')
      .map((format) => format.trim())
      .filter(Boolean)
      .map((format) => `original-format:${format}`)
      .join('|');

    if (formatFacets) {
      params.set('fa', formatFacets);
    }
  }

  return params;
};

export const buildCreativeWorksCatalogSearchUrl = (
  query: string,
  options?: {
    originalFormat?: string;
    page?: number;
  }
): string => {
  const params = buildSearchParams(query, {
    originalFormat: options?.originalFormat,
    page: options?.page,
  });
  return `${CREATIVE_WORKS_SEARCH_BASE}?${params.toString()}`;
};

export const buildCreativeWorksCatalogSearchByIdUrl = (
  itemId: string
): string => {
  const params = buildSearchParams(itemId, {
    pageSize: CREATIVE_WORKS_SEARCH_ID_LOOKUP_SIZE,
    page: 1,
  });
  return `${CREATIVE_WORKS_SEARCH_BASE}?${params.toString()}`;
};

const FLATTENED_RESULTS_KEY_PREFIX = 'results.';

const PREFERRED_FLATTENED_RESULTS_KEYS = [
  'results.url',
  'results.title',
] as const;

/**
 * Normalizes catalog search JSON into a `results` array.
 * With lean `at=results.*` selectors, catalog returns parallel `results.<field>`
 * arrays of full hit objects instead of a top-level `results` key.
 */
export const parseCatalogSearchResultsFromResponse = (
  data: CreativeWorksCatalogSearchApiResponse
): CreativeWorksCatalogResultRaw[] => {
  if (Array.isArray(data.results)) {
    return data.results;
  }

  for (const key of PREFERRED_FLATTENED_RESULTS_KEYS) {
    const rows = data[key];
    if (Array.isArray(rows) && rows.length > 0) {
      return rows as CreativeWorksCatalogResultRaw[];
    }
  }

  const flattenedKey = Object.keys(data).find((key) => {
    if (!key.startsWith(FLATTENED_RESULTS_KEY_PREFIX)) {
      return false;
    }
    const rows = data[key];
    return Array.isArray(rows) && rows.length > 0;
  });

  if (flattenedKey) {
    return data[flattenedKey] as CreativeWorksCatalogResultRaw[];
  }

  return [];
};

export const fetchCreativeWorksCatalogSearchPage = async (
  url: string
): Promise<CreativeWorksCatalogSearchPageResponse> => {
  const data = await creativeWorksCatalogRequests.fetchCatalogSearch(url);
  return {
    results: parseCatalogSearchResultsFromResponse(data),
    pagination: data.pagination,
  };
};

/**
 * Runs a Creative Works catalog search using catalog query and facet parameters only.
 */
export const searchCreativeWorksCatalogRaw = async (
  query: string,
  originalFormat?: string,
  page = 1
): Promise<CreativeWorksCatalogSearchPageResponse> => {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return { results: [] };
  }

  return fetchCreativeWorksCatalogSearchPage(
    buildCreativeWorksCatalogSearchUrl(trimmedQuery, { originalFormat, page })
  );
};
